import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { queryKeys, useAuthenticatedClient } from "./queries";
import type { components } from "./schema";

// Revenue, Net, Profit and Margin over time and by who and what sold (USA-1275).
export type AnalyticsSummary = components["schemas"]["AnalyticsSummary"];
export type AnalyticsMetrics = components["schemas"]["AnalyticsMetrics"];
export type AnalyticsBucket = components["schemas"]["AnalyticsBucket"];
export type AnalyticsBreakdown = components["schemas"]["AnalyticsBreakdown"];
export type AnalyticsBreakdownRow = components["schemas"]["AnalyticsBreakdownRow"];
export type AnalyticsOrders = components["schemas"]["AnalyticsOrders"];
export type AnalyticsDimension = AnalyticsBreakdown["dimension"];

export type AnalyticsChannel = "online" | "dropship" | "quote_builder";
export type AnalyticsFreight = "ltl" | "parcel";

export interface AnalyticsScope {
  start?: string;
  end?: string;
  sales_person?: string;
  channel?: AnalyticsChannel;
  manufacturer?: string;
  category?: string;
  freight?: AnalyticsFreight;
}

export function useAnalyticsSummary(scope: AnalyticsScope) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.analytics.summary(scope),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET("/dashboard/analytics/summary", {
        params: { query: scope },
      });
      if (error) throw error;
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useAnalyticsBreakdown(
  scope: AnalyticsScope & { dimension: AnalyticsDimension; limit?: number; category_level?: number },
) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.analytics.breakdown(scope),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET("/dashboard/analytics/breakdown", {
        params: { query: scope },
      });
      if (error) throw error;
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useAnalyticsOrders(
  scope: AnalyticsScope & {
    dimension?: AnalyticsDimension;
    key?: string;
    page?: number;
    limit?: number;
    category_level?: number;
  },
  enabled: boolean,
) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.analytics.orders(scope),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET("/dashboard/analytics/orders", {
        params: { query: scope },
      });
      if (error) throw error;
      return data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}
